import React, { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { Stepper } from "react-form-stepper";
import { useNavigate } from "react-router-dom";
import { tenderService } from "../../services/api";
import TenderFormDetails from "../../components/club/TenderFormDetails";
import TenderFormRequirements from "../../components/club/TenderFormRequirements";
import ScheduleRequestModal from "../../components/club/ScheduleRequestModal";
import {
  PLAYER_POSITIONS,
  STAFF_POSITIONS,
  COACH_POSITIONS,
  AGE_CHOICES,
  FOOT_CHOICES,
  TYPE_OF_TENDER_CHOICES,
  STAFF_TENDER_TYPE_CHOICES,
  SALARY_TYPE_CHOICES,
  MARKET_VALUE_CHOICES,
  TENDER_CATEGORY_CHOICES,
  GAMES_PLAYED_CHOICES,
  LANGUAGE_CHOICES,
  EXPERIENCE_CHOICES,
  RECRUITING_FOR_CHOICES,
} from "../../constants/tenderChoices";

const CreateTender = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // T004: inline errors keyed by error group ("salary", "market_value",
  // "position", …) so the offending field shows its own message instead of the
  // failure being swallowed into a generic toast.
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  // The wizard's finish is a three-way choice: post it now, park it as a
  // draft, or give it a date. The picker below supplies the date for the third
  // one; nothing else about the wizard changes.
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [formData, setFormData] = useState({
    category: "pro", // New field for Pro/Youth selection
    tenderFor: TENDER_CATEGORY_CHOICES[0].value,
    recruitingFor: "professional", // Maps to professional/youth based on category
    tenderType: TYPE_OF_TENDER_CHOICES[0].value,
    staffTenderType: STAFF_TENDER_TYPE_CHOICES[0].value,
    // EUR, in parity with the marketplace form (DECISION-010, Marin). The list
    // order happens to start at USD, which made every untouched club request a
    // dollar request without the admin ever choosing one. Option list unchanged.
    currency: "EUR",
    maxTransferFee: "",
    salaryType: SALARY_TYPE_CHOICES[0].value,
    salaryCalculationType: "net", // New field for Gross/Net salary selection
    netSalary: "",
    salaryRange: { min: "", max: "" },
    hasBonus: false,
    position: "",
    // No pre-selected foot (Marin, DECISION-008): parity with the marketplace
    // form — "" = not specified = both feet, and the payload omits the key.
    foot: "",
    minHeight: "",
    minAge: "",
    maxAge: "",
    age: AGE_CHOICES[0].value,
    marketValueType: MARKET_VALUE_CHOICES[0].value,
    marketValue: "",
    marketValueRange: { min: "", max: "" },
    description: "",
    otherRequirements: "",
    documents: [],
    matchesPlayedLastSeason: "",
    matchesPlayedLastHalfSeason: "",
    experience: "",
    languageSkills: [],
    // New fields for different tender types
    onlyEuPlayer: false,
    // DECISION-010: a silent "free agents only" cuts most of the market out of
    // the request without the club noticing. Default No, like the agent form.
    onlyFreePlayers: false,
    loanFee: false,
    loanFeeAmount: "", // New field for loan fee amount
    loanWithOptionToBuy: true,
    durationInMonths: "",
    trialPeriod: { start: "", end: "" },
    travel: false,
    housing: false,
    foodCovered: false,
    offeringAdditionalBenefits: true,
    gamesPlayedIn: GAMES_PLAYED_CHOICES[0].value,
    staffPosition: "",
    toleranceValue: null, // New field for tolerance percentage
  });

  const steps = useMemo(
    () => [
      { key: "details", label: "Request Details" },
      { key: "requirements", label: "Requirements" },
    ],
    []
  );

  // T004: which wizard step renders which error group, so a validation error
  // navigates to the field it complains about. Step 0 = Request Details
  // (salary), step 1 = Requirements (position, market value). The step-0 salary
  // error used to be shown only on step 1, where the salary fields are hidden.
  const ERROR_GROUP_STEP = { salary: 0, market_value: 1, position: 1 };

  // Backend (DRF) 400 keys → the error group rendered inline in the form.
  const DRF_KEY_TO_GROUP = {
    salary: "salary",
    salary_type: "salary",
    net_salary_from: "salary",
    net_salary_to: "salary",
    gross_salary_from: "salary",
    gross_salary_to: "salary",
    fix_net_salary: "salary",
    fix_gross_salary: "salary",
    market_value: "market_value",
    market_value_type: "market_value",
    market_value_from: "market_value",
    market_value_to: "market_value",
    fix_market_value: "market_value",
    position: "position",
  };

  // Frontend validation failure: banner + toast + inline error at the field,
  // and jump to the step that actually renders that field.
  const failValidation = (group, message) => {
    setFieldErrors({ [group]: message });
    setError(message);
    toast.error(message);
    if (ERROR_GROUP_STEP[group] !== undefined) {
      setActiveStep(ERROR_GROUP_STEP[group]);
    }
  };

  const handleNext = () => {
    if (loading) return;
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    if (loading) return;
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  // `intent` is what the club chose at the finish line, and it is the ONLY
  // thing that differs between the three paths:
  //   "publish"   post it now      -> nothing sent, exactly as before
  //   "draft"     save as draft    -> publish_mode 'draft'
  //   "schedule"  give it a date   -> publish_mode 'schedule' + the date
  // The club states an INTENT, never a status: `status` is not writable over
  // the API at all, so that no client can post itself a 'deal' or resurrect an
  // expired request. A scheduled request is a draft that happens to carry a
  // date, never a third kind of row.
  const handleSubmit = async (e, intent = "publish", scheduledPublishAt = null) => {
    e?.preventDefault?.();
    if (activeStep < steps.length - 1) return;
    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      // Frontend validations aligned with API expectations
      // Salary validations. An untouched salary range (the default
      // "Salary between" with both fields still empty) means "not specified" —
      // it is omitted from the payload below, so only validate once the user
      // actually entered something.
      if (formData.salaryType === "salary_between") {
        const minStr = (formData.salaryRange?.min || "").trim();
        const maxStr = (formData.salaryRange?.max || "").trim();
        if (minStr !== "" || maxStr !== "") {
          if (minStr === "" || maxStr === "") {
            failValidation(
              "salary",
              `Both ${formData.salaryCalculationType === "gross" ? "gross" : "net"} salary from and to are required for range type`
            );
            return;
          }
          const minSalary = Number(minStr);
          const maxSalary = Number(maxStr);
          if (!Number.isFinite(minSalary) || !Number.isFinite(maxSalary)) {
            failValidation(
              "salary",
              `Please enter valid numbers for ${formData.salaryCalculationType === "gross" ? "gross" : "net"} salary range`
            );
            return;
          }
          if (minSalary >= maxSalary) {
            failValidation(
              "salary",
              `${formData.salaryCalculationType === "gross" ? "Gross" : "Net"} salary from must be less than salary to`
            );
            return;
          }
        }
      } else if (formData.salaryType === "fixed_amount") {
        const fixedStr = (formData.netSalary || "").trim();
        if (fixedStr === "") {
          failValidation(
            "salary",
            `Fixed ${formData.salaryCalculationType === "gross" ? "gross" : "net"} salary is required for fixed type`
          );
          return;
        }
        if (!Number.isFinite(Number(fixedStr))) {
          failValidation(
            "salary",
            `Please enter a valid number for fixed ${formData.salaryCalculationType === "gross" ? "gross" : "net"} salary`
          );
          return;
        }
      }

      // Player-specific validations
      if (formData.tenderFor === "player") {
        // Only validate market value if user has entered values
        if (formData.marketValueType === "market_value_range") {
          const minStr = (formData.marketValueRange?.min || "").trim();
          const maxStr = (formData.marketValueRange?.max || "").trim();
          
          // Only validate if at least one value is provided
          if (minStr !== "" || maxStr !== "") {
            if (minStr === "" || maxStr === "") {
              failValidation(
                "market_value",
                "Both market value from and to are required for range type"
              );
              return;
            }
            const minVal = Number(minStr);
            const maxVal = Number(maxStr);
            if (!Number.isFinite(minVal) || !Number.isFinite(maxVal)) {
              failValidation(
                "market_value",
                "Please enter valid numbers for market value"
              );
              return;
            }
            if (minVal >= maxVal) {
              failValidation(
                "market_value",
                "Market value from must be less than market value to"
              );
              return;
            }
          }
        } else if (formData.marketValueType === "market_value_fixed") {
          const fixedStr = (formData.marketValue || "").trim();
          // Only validate if value is provided
          if (fixedStr !== "" && !Number.isFinite(Number(fixedStr))) {
            failValidation(
              "market_value",
              "Please enter a valid number for fixed market value"
            );
            return;
          }
        }

        if (formData.tenderType === "trial") {
          const start = formData.trialPeriod?.start || "";
          const end = formData.trialPeriod?.end || "";
          if (!start || !end) {
            const message = "Trial start and end dates are required for trial requests.";
            setError(message);
            toast.error(message);
            return;
          }
        }

        if (formData.tenderType === "loan" && formData.loanFee) {
          const feeStr = (formData.loanFeeAmount || "").trim();
          if (feeStr === "") {
            const message = "Loan fee amount is required when loan fee is selected.";
            setError(message);
            toast.error(message);
            return;
          }
          if (!Number.isFinite(Number(feeStr))) {
            const message = "Please enter a valid number for loan fee amount";
            setError(message);
            toast.error(message);
            return;
          }
        }
      }

      // Staff-specific validations
      if (formData.tenderFor === "staff" && formData.staffTenderType === "contract") {
        const monthsStr = (formData.durationInMonths || "").trim();
        if (monthsStr === "") {
          const message = "Duration in months is required for contract requests";
          setError(message);
          toast.error(message);
          return;
        }
        if (!Number.isFinite(Number(monthsStr)) || Number(monthsStr) <= 0) {
          const message = "Please enter a valid duration in months (> 0)";
          setError(message);
          toast.error(message);
          return;
        }
      }

      // T004: an untouched salary range (the default "Salary between" with both
      // fields empty) means "not specified" — omit the whole salary block
      // instead of serialising Number("") === 0 into a 0/0 range the backend
      // rejects. Same omit-empty-numeric-optionals contract as
      // AgentTenderForm.pruneEmptyNumerics
      // (FE PR #156, src/components/agent-tender/AgentTenderForm.js:254-285).
      const salaryUntouched =
        formData.salaryType === "salary_between" &&
        (formData.salaryRange?.min || "").trim() === "" &&
        (formData.salaryRange?.max || "").trim() === "";

      // Base payload
      const payload = {
        tender_category: formData.tenderFor,
        recruiting_for: formData.tenderFor === "player" ? formData.recruitingFor : "professional",
        type_of_tender:
          formData.tenderFor === "staff"
            ? formData.staffTenderType
            : formData.tenderType,
        currency: formData.currency,
        position: formData.position || null,
        ...(salaryUntouched
          ? {}
          : {
              salary_type: formData.salaryType,
              fix_net_salary:
                formData.salaryType === "fixed_amount" && formData.salaryCalculationType === "net"
                  ? Number(formData.netSalary)
                  : null,
              fix_gross_salary:
                formData.salaryType === "fixed_amount" && formData.salaryCalculationType === "gross"
                  ? Number(formData.netSalary)
                  : null,
              net_salary_from:
                formData.salaryType === "salary_between" && formData.salaryCalculationType === "net"
                  ? Number(formData.salaryRange.min)
                  : null,
              net_salary_to:
                formData.salaryType === "salary_between" && formData.salaryCalculationType === "net"
                  ? Number(formData.salaryRange.max)
                  : null,
              gross_salary_from:
                formData.salaryType === "salary_between" && formData.salaryCalculationType === "gross"
                  ? Number(formData.salaryRange.min)
                  : null,
              gross_salary_to:
                formData.salaryType === "salary_between" && formData.salaryCalculationType === "gross"
                  ? Number(formData.salaryRange.max)
                  : null,
            }),
        bonus: formData.hasBonus,
        min_age: formData.minAge ? Number(formData.minAge) : null,
        max_age: formData.maxAge ? Number(formData.maxAge) : null,
        description: formData.description,
        key_requirements: formData.otherRequirements,
        matches_played_last_season: formData.matchesPlayedLastSeason
          ? parseInt(formData.matchesPlayedLastSeason)
          : 0,
        matches_played_last_half_season: formData.matchesPlayedLastHalfSeason
          ? parseInt(formData.matchesPlayedLastHalfSeason)
          : 0,
        experience: formData.experience || null,
        language_skills: formData.languageSkills,
        // Document will be handled separately in FormData if present
        // New API fields
        only_eu_player: formData.onlyEuPlayer || false,
        only_free_players: formData.onlyFreePlayers || false,
        tolerance_value: formData.toleranceValue
          ? parseInt(formData.toleranceValue)
          : null,
      };

      // Drafts. "Post now" sends nothing extra: `publish_mode` is optional and
      // an absent one means "now", so the request the platform has always
      // created is created byte-for-byte as it always was.
      //
      // `scheduled_publish_at` rides along ONLY with the schedule intent —
      // that is the one case where the backend requires it (and requires it to
      // be in the future). With any other intent it is dropped server-side
      // anyway, so sending a null would be noise the FormData branch below
      // would have to filter out again.
      if (intent === "draft" || intent === "schedule") {
        payload.publish_mode = intent === "schedule" ? "schedule" : "draft";
        if (intent === "schedule") {
          payload.scheduled_publish_at = scheduledPublishAt;
        }
      }

      // Add player-specific fields
      if (formData.tenderFor === "player") {
        // T004: the untouched default ("Market value between" with both fields
        // empty, or "Fixed Amount" with no value) used to be serialised as a
        // 0/0 range → backend 400 "Both market value from and to are required
        // for range type", swallowed into a generic toast. Omit the block
        // instead (same contract as AgentTenderForm, FE PR #156).
        const marketValueUntouched =
          (formData.marketValueType === "market_value_range" &&
            (formData.marketValueRange?.min || "").trim() === "" &&
            (formData.marketValueRange?.max || "").trim() === "") ||
          (formData.marketValueType === "market_value_fixed" &&
            (formData.marketValue || "").trim() === "");
        if (!marketValueUntouched) {
          payload.market_value_type = formData.marketValueType;
          payload.fix_market_value =
            formData.marketValueType === "market_value_fixed"
              ? Number(formData.marketValue)
              : null;
          payload.market_value_from =
            formData.marketValueType === "market_value_range"
              ? Number(formData.marketValueRange.min)
              : null;
          payload.market_value_to =
            formData.marketValueType === "market_value_range"
              ? Number(formData.marketValueRange.max)
              : null;
        }
        // DECISION-008: an unchosen foot is omitted, never sent as "".
        if (formData.foot) {
          payload.foot = formData.foot;
        }
        payload.minimum_height = formData.minHeight
          ? parseInt(formData.minHeight)
          : null;

        // Maximum transfer fee for BUY tenders
        if (formData.tenderType === "buy") {
          payload.maximum_transfer_fee = formData.maxTransferFee
            ? Number(formData.maxTransferFee)
            : null;
        }

        // Loan-specific fields for player tenders
        if (formData.tenderType === "loan") {
          payload.has_loan_fee = formData.loanFee || false;
          payload.loan_fee_amount =
            formData.loanFee && formData.loanFeeAmount
              ? Number(formData.loanFeeAmount)
              : null;
          payload.loan_with_option_to_buy =
            formData.loanWithOptionToBuy || false;
        }

        // Trial-specific fields for player tenders
        if (formData.tenderType === "trial") {
          payload.trial_period_start_date = formData.trialPeriod.start || null;
          payload.trial_period_end_date = formData.trialPeriod.end || null;
          payload.travel = formData.travel || false;
          payload.housing = formData.housing || false;
          payload.food_covered = formData.foodCovered || false;
        }
      }

      // Add staff-specific fields
      if (formData.tenderFor === "staff") {
        if (formData.staffTenderType === "contract") {
          payload.temporary_duration = formData.durationInMonths
            ? parseInt(formData.durationInMonths)
            : 0;
        }
        payload.other_staff_position = formData.staffPosition || null;
        payload.offering_additional_benefits =
          formData.offeringAdditionalBenefits || false;
      }

      // Handle file upload - convert to FormData if document is present
      let response;
      if (formData.documents && formData.documents.length > 0) {
        // Create FormData for file upload
        const formDataToSend = new FormData();
        
        // Add all payload fields to FormData
        Object.keys(payload).forEach(key => {
          if (payload[key] !== null && payload[key] !== undefined) {
            if (Array.isArray(payload[key])) {
              // Handle arrays (like language_skills)
              payload[key].forEach(item => {
                formDataToSend.append(key, item);
              });
            } else {
              formDataToSend.append(key, payload[key]);
            }
          }
        });
        
        // Add the document file
        formDataToSend.append('document', formData.documents[0]);
        
        response = await tenderService.createTender(formDataToSend);
      } else {
        // Send regular JSON payload
        response = await tenderService.createTender(payload);
      }

      if (response.success) {
        // The 201 answers with the full club DETAIL representation of the new
        // row (id, status, scheduled_publish_at) rather than the write shape it
        // used to echo back, so where the club is sent next is read off what
        // the server actually created — no second round trip to find out, and
        // no guess. The intent is only the fallback for a response that does
        // not carry the field, so an old/mocked backend still behaves.
        const created = response.data;
        const landedAsDraft =
          created && typeof created.status === "string"
            ? created.status === "draft"
            : intent !== "publish";
        const carriesADate =
          created && "scheduled_publish_at" in created
            ? Boolean(created.scheduled_publish_at)
            : intent === "schedule";

        if (!landedAsDraft) {
          navigate("/club/tenders");
        } else {
          // A saved draft has to be findable, so the list opens on the tab it
          // just landed in instead of on Active, where it is not.
          toast.success(
            carriesADate
              ? "Draft saved with a publish date."
              : "Saved as a draft."
          );
          navigate("/club/tenders", { state: { lifecycle: "drafts" } });
        }
      } else {
        // T004: DRF validation 400s arrive as {field: ["msg", ...]}. Surface
        // them inline at the offending field and jump to the wizard step that
        // renders it, instead of only a generic toast.
        const beErrors = response.fieldErrors;
        if (beErrors && typeof beErrors === "object" && !Array.isArray(beErrors)) {
          const mapped = {};
          let firstStep;
          Object.entries(beErrors).forEach(([key, messages]) => {
            const msg = Array.isArray(messages) ? messages[0] : messages;
            if (typeof msg !== "string" || !msg) return;
            const group = DRF_KEY_TO_GROUP[key] || key;
            if (!mapped[group]) mapped[group] = msg;
            if (firstStep === undefined && ERROR_GROUP_STEP[group] !== undefined) {
              firstStep = ERROR_GROUP_STEP[group];
            }
          });
          setFieldErrors(mapped);
          if (firstStep !== undefined) setActiveStep(firstStep);
        }
        setError(response.error);
        toast.error(response.error || "Failed to create request");
      }
    } catch (err) {
      const message = "Failed to create request. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="sm:px-12 px-4 py-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/club/tenders")}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Go back to Club Requests"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.8337 6.99935H1.16699M1.16699 6.99935L7.00033 12.8327M1.16699 6.99935L7.00033 1.16602"
                stroke="#2C2C2C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-gray-900">Create Request</h1>
        </div>

        {/* Stepper + top navigation controls in one row */}
        <div className="mb-4 grid lg:grid-cols-3 items-center">
          <div className="justify-self-start" />
          <div className="justify-self-center w-full max-w-2xl">
            <Stepper
              steps={steps.map((s) => ({ label: s.label }))}
              activeStep={activeStep}
              connectorStateColors
              styleConfig={{
                activeBgColor: "#2283FF",
                completedBgColor: "#2283FF",
                inactiveBgColor: "#E6E6E6",
                activeTextColor: "#FFFFFF",
                completedTextColor: "#FFFFFF",
                inactiveTextColor: "#1E1E1E",
                size: "28px",
                borderRadius: "50%",
              }}
              connectorStyleConfig={{
                activeColor: "#2283FF",
                completedColor: "#2283FF",
                disabledColor: "#E6E6E6",
                size: 1,
              }}
            />
          </div>
          <div className="justify-self-end flex items-center gap-3">
            {activeStep > 0 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={loading}
                className="px-4 py-2 text-sm text-[#1E1E1E] disabled:opacity-50 border border-[#E6E6E6] shadow-md rounded-lg cursor-pointer"
                aria-label="Go to previous step"
              >
                Back
              </button>
            )}
            {activeStep < steps.length - 1 && (
              <button
                type="button"
                onClick={handleNext}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 cursor-pointer"
                aria-label="Go to next step"
              >
                Next
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-500 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {activeStep === 0 && (
            <div className="bg-gray-50 rounded-lg p-0.5">
              <TenderFormDetails
                formData={formData}
                setFormData={setFormData}
                fieldErrors={fieldErrors}
                choices={{
                  tenderTypes: TYPE_OF_TENDER_CHOICES,
                  staffTenderTypes: STAFF_TENDER_TYPE_CHOICES,
                  salaryTypes: SALARY_TYPE_CHOICES,
                  recruitingForChoices: RECRUITING_FOR_CHOICES,
                }}
              />
            </div>
          )}

          {activeStep === 1 && (
            <div className="bg-gray-50 rounded-lg p-0.5">
              <TenderFormRequirements
                formData={formData}
                setFormData={setFormData}
                fieldErrors={fieldErrors}
                choices={{
                  playerPositions: PLAYER_POSITIONS,
                  // Coach roles + non-coach staff in one "Staff Position" list.
                  // Before commit 9bc7d52 ("fix tender filters", Oct 2025)
                  // STAFF_POSITIONS itself contained the coach roles
                  // (head_coach, assistant_coach, …); that commit split them
                  // into COACH_POSITIONS for the filter UI but did NOT update
                  // this create form, silently dropping coach roles from tender
                  // creation. Coaches are stored as tender_category='staff'
                  // with position='head_coach' etc., and the agent Coaches tab
                  // filters tender_category='staff' AND position icontains
                  // 'coach' — so these MUST be selectable here for a coach
                  // request to exist and show under Coaches. Coaches first to
                  // match the original ordering.
                  staffPositions: [...COACH_POSITIONS, ...STAFF_POSITIONS],
                  footChoices: FOOT_CHOICES,
                  ageChoices: AGE_CHOICES,
                  marketValueTypes: MARKET_VALUE_CHOICES,
                  gamesPlayedChoices: GAMES_PLAYED_CHOICES,
                  languageChoices: LANGUAGE_CHOICES,
                  experienceChoices: EXPERIENCE_CHOICES,
                }}
              />
            </div>
          )}
          {/* Bottom Navigation - Always visible. flex-wrap because the finish
              line now carries four controls and must not push off a 320px
              screen. */}
          <div className="flex flex-wrap gap-3 mt-6">
            {activeStep > 0 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={loading}
                className="px-4 py-2 text-[#1E1E1E] disabled:opacity-50 border border-[#E6E6E6] shadow-md rounded-lg cursor-pointer"
                aria-label="Go to previous step"
              >
                Back
              </button>
            )}
            {activeStep < steps.length - 1 && (
              <button
                type="button"
                onClick={handleNext}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 cursor-pointer"
                aria-label="Go to next step"
              >
                Next
              </button>
            )}
            {activeStep === steps.length - 1 && (
              /* The finish line is a three-way choice: Post now · Save as
                 draft · Schedule. "Post now" stays the form's submit button,
                 so pressing Enter still does what it always did. The other two
                 are plain buttons calling the same handler with their own
                 intent — one submit path, three outcomes. */
              <>
                <button
                  type="submit"
                  disabled={loading}
                  className="min-h-[44px] px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 cursor-pointer"
                  aria-label="Post request now"
                >
                  {loading ? "Working..." : "Post now"}
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={(e) => handleSubmit(e, "draft")}
                  className="min-h-[44px] px-4 py-2 text-[#1E1E1E] text-sm font-medium border border-[#E6E6E6] shadow-md rounded-lg hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
                  aria-label="Save request as a draft"
                >
                  Save as draft
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => setShowScheduleModal(true)}
                  className="min-h-[44px] px-4 py-2 text-[#1E1E1E] text-sm font-medium border border-[#E6E6E6] shadow-md rounded-lg hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
                  aria-label="Schedule this request"
                >
                  Schedule
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/club/tenders")}
                  className="min-h-[44px] px-4 py-2 text-[#1E1E1E] text-sm border border-[#E6E6E6] shadow-md rounded-lg cursor-pointer"
                  aria-label="Cancel and go back"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </form>

        {/* Same picker the draft card opens — one schedule dialog, not two. */}
        <ScheduleRequestModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onConfirm={(date) => {
            setShowScheduleModal(false);
            handleSubmit(null, "schedule", date.toISOString());
          }}
          requestTitle="New request"
          isSaving={loading}
        />
      </div>
    </div>
  );
};

export default CreateTender;
